/**
 * PATH: apps/qa/src/pages/record-detail/participantRoles.ts
 * PURPOSE: Pure role metadata + error shaping for the Team (participants) UI.
 * WHY: Keeps the modal presentational; the role vocabulary must mirror the
 *      backend qa.models.RoleOnJob choices exactly, and API errors arrive in
 *      three different DRF shapes that need one readable string.
 */

import type { RoleOnJob } from '@/types/qa'

/** Mirrors backend qa.models.RoleOnJob — keep in sync (contract pin in test). */
export const ROLE_OPTIONS: Array<{ value: RoleOnJob; label: string; hint: string }> = [
  { value: 'installer', label: 'Installer', hint: 'Captures evidence and completes checklist items' },
  { value: 'supervisor', label: 'Supervisor', hint: 'Installer duties plus area sign-off' },
  { value: 'pm', label: 'Project Manager', hint: 'Full job QA management incl. participants' },
  { value: 'qa_manager', label: 'QA Manager', hint: 'Full job QA management incl. participants' },
  { value: 'builder_rep', label: 'Builder Rep', hint: 'External builder-side reviewer' },
  { value: 'auditor', label: 'Auditor', hint: 'Read + audit trail access' },
  { value: 'viewer', label: 'Viewer', hint: 'Read-only' },
]

const ROLE_LABELS: Record<string, string> = Object.fromEntries(
  ROLE_OPTIONS.map(r => [r.value, r.label]),
)

export function roleLabel(role: string): string {
  return ROLE_LABELS[role] ?? role.replace(/_/g, ' ')
}

/**
 * Flatten a DRF error payload into one readable line.
 * Handles: {detail: "..."} (403s), {field: ["msg"]} (validation),
 * plain strings, and anything else -> fallback.
 */
export function extractApiError(err: unknown, fallback: string): string {
  const data = (err as { response?: { data?: unknown } })?.response?.data
  if (typeof data === 'string' && data.trim()) return data
  if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>
    if (typeof obj.detail === 'string') return obj.detail
    for (const value of Object.values(obj)) {
      if (typeof value === 'string' && value.trim()) return value
      if (Array.isArray(value) && typeof value[0] === 'string') return value[0]
    }
  }
  const message = (err as { message?: string })?.message
  return message || fallback
}
